/**
 * Die Anmeldung des Portals gegen Keycloak (`UI-001`, Vertrag aus `IAM-001`).
 *
 * **Was hier steht und was nicht.** Der Weg selbst — PKCE, Zustand,
 * Einmalwert, Token-Tausch — liegt in `@hwc/auth`, weil die Monteur-PWA
 * denselben braucht. Hier steht nur, was am Browser hängt: wo die angefangene
 * Anmeldung liegt, während der Benutzer bei Keycloak ist, und wie aus der
 * Laufzeitkonfiguration ein Anbieter wird.
 *
 * **Die Anmeldung ist keine Berechtigung.** `01` §1.11 ist eindeutig: Die ACL
 * wird am Quellsystem geprüft, nicht im Portal entschieden. Was hier
 * herauskommt, bestimmt, welcher Name in der Kopfzeile steht und welche
 * Menüpunkte erscheinen — nicht, was jemand sehen darf. Ein verstecktes Menü
 * hat noch nie etwas geschützt.
 */

import {
  anmeldeAnfrage,
  abmeldeUrl,
  keycloakAnbieter,
  leseRueckkehr,
  liesAnsprueche,
  tauscheCode,
  type AngefangeneAnmeldung,
  type IdentitaetsAnbieter,
  type Tokensatz,
} from "@hwc/auth";
import type { PortalKonfiguration } from "@hwc/config";

/**
 * Der Rückweg, den Keycloak kennt.
 *
 * **Eine feste Adresse, kein Muster.** `infra/keycloak/clients.yaml` führt
 * genau diesen Pfad, und die Begründung steht dort: Ein Platzhalter im Rückweg
 * heißt, dass jeder Pfad dieser Domain den Autorisierungscode entgegennehmen
 * darf.
 */
export const RUECKWEG_PFAD = "/auth/callback";

/**
 * Wo die angefangene Anmeldung liegt, während der Benutzer bei Keycloak ist.
 *
 * **`sessionStorage`, nicht `localStorage`.** Der Verifizierer ist ein
 * Einmalgeheimnis für genau diese Anmeldung; er hat in einem Speicher, der
 * Wochen überdauert und von jedem Tab derselben Herkunft gelesen wird, nichts
 * verloren. Nach dem Tausch wird er gelöscht — ein zweiter Versuch mit
 * demselben Code scheitert ohnehin.
 */
const SCHLUESSEL = "hwc.anmeldung.angefangen";
const TOKEN_SCHLUESSEL = "hwc.anmeldung.token";

export interface Angemeldet {
  readonly name: string;
  readonly benutzername: string;
  readonly gruppen: readonly string[];
  readonly ansprueche: Record<string, unknown>;
  readonly token: Tokensatz;
}

/**
 * Baut den Anbieter aus der Laufzeitkonfiguration — oder gibt auf.
 *
 * Ohne `domains.auth` oder `realm` gibt es keine Anmeldung. Das ist kein
 * Fehler, sondern der Zustand eines Stacks, dessen Identitätsanbieter noch
 * nicht steht; `undefined` sagt das, eine geratene Adresse würde es verbergen.
 *
 * **Zwei weitere Fälle liefern ebenfalls keinen Anbieter**, und der erste
 * hat am 07.09.2026 die Oberflächenprüfung angehalten:
 *
 * `stage: "dev"` — der Demostand. Seine Entwicklungskonfiguration **trägt**
 * `realm` und `domains.auth`, damit die Shell die Adressfelder kennt. Eine
 * Anmeldung daraus abzuleiten sperrte `pnpm demo` hinter einen
 * Identitätsanbieter, den es nicht gibt: Acht Prüfungen liefen in einen
 * Timeout, weil die Anwendung nie erschien.
 *
 * `.invalid` — die Adresse ist nach RFC 2606 ausdrücklich keine. Der
 * Demobestand benutzt sie für Dienste, die es nicht gibt
 * (`packages/demo-data/src/quelle.ts` hält es genauso), und ein Anmeldeweg
 * dorthin führte in eine Fehlerseite statt zu Keycloak.
 */
export function anbieterAus(
  konfiguration: PortalKonfiguration,
): IdentitaetsAnbieter | undefined {
  const auth = konfiguration.domains?.auth;
  const realm = konfiguration.realm;
  if (!auth || !realm) return undefined;
  if (konfiguration.stage === "dev") return undefined;
  if (auth.endsWith(".invalid") || auth.includes(".invalid:")) return undefined;
  return keycloakAnbieter({
    aussteller: `https://${auth}/realms/${realm}`,
    empfaenger: "portal",
  });
}

function ablage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Ein Browser, der Speicher verbietet, ist kein Fehlerfall der Anwendung.
    return undefined;
  }
}

export function merkeAngefangen(a: AngefangeneAnmeldung): void {
  ablage()?.setItem(SCHLUESSEL, JSON.stringify(a));
}

export function liesAngefangen(): AngefangeneAnmeldung | undefined {
  const roh = ablage()?.getItem(SCHLUESSEL);
  if (!roh) return undefined;
  try {
    return JSON.parse(roh) as AngefangeneAnmeldung;
  } catch {
    return undefined;
  }
}

export function vergissAngefangen(): void {
  ablage()?.removeItem(SCHLUESSEL);
}

export function merkeToken(t: Tokensatz): void {
  ablage()?.setItem(TOKEN_SCHLUESSEL, JSON.stringify(t));
}

export function liesToken(): Tokensatz | undefined {
  const roh = ablage()?.getItem(TOKEN_SCHLUESSEL);
  if (!roh) return undefined;
  try {
    return JSON.parse(roh) as Tokensatz;
  } catch {
    return undefined;
  }
}

export function vergissToken(): void {
  ablage()?.removeItem(TOKEN_SCHLUESSEL);
}

/** Schickt den Browser zur Anmeldung. Kehrt nicht zurück. */
export async function starteAnmeldung(
  anbieter: IdentitaetsAnbieter,
  herkunft: string,
  zurueckNach: string,
): Promise<void> {
  const { url, angefangen } = await anmeldeAnfrage(anbieter, {
    rueckweg: herkunft + RUECKWEG_PFAD,
    zurueckNach,
  });
  merkeAngefangen(angefangen);
  window.location.assign(url);
}

export type Rueckkehrergebnis =
  | { readonly art: "angemeldet"; readonly wer: Angemeldet; readonly weiter: string }
  | { readonly art: "fehler"; readonly grund: string }
  | { readonly art: "keine" };

/**
 * Verarbeitet, was in der Adresszeile steht, nachdem Keycloak zurückschickt.
 *
 * **Die angefangene Anmeldung wird in jedem Fall vergessen** — auch wenn es
 * schiefging. Ein liegengebliebener Verifizierer wäre bei der nächsten
 * Anmeldung der falsche, und der Fehler sähe dann aus wie ein Problem mit
 * Keycloak.
 */
export async function verarbeiteRueckkehr(
  anbieter: IdentitaetsAnbieter,
  herkunft: string,
  suche: string,
): Promise<Rueckkehrergebnis> {
  const r = leseRueckkehr(suche);
  if (r.art === "keine") return { art: "keine" };

  const angefangen = liesAngefangen();
  vergissAngefangen();

  if (r.art === "fehler") return { art: "fehler", grund: r.grund };
  if (!angefangen) {
    return {
      art: "fehler",
      grund:
        "Zu dieser Rückkehr gibt es keine angefangene Anmeldung in diesem " +
        "Browserfenster.",
    };
  }

  let token: Tokensatz;
  try {
    token = await tauscheCode(anbieter, angefangen, r, herkunft + RUECKWEG_PFAD);
  } catch (ursache) {
    return {
      art: "fehler",
      grund: ursache instanceof Error ? ursache.message : String(ursache),
    };
  }

  const ansprueche = liesAnsprueche(token.idToken);

  /*
   * **Der Einmalwert wird geprüft, nicht nur mitgeschickt.**
   *
   * Er steht im ID-Token, und stimmt er nicht mit dem überein, den diese
   * Anmeldung erzeugt hat, gehört das Token nicht hierher — es kann anderswo
   * erbeutet und untergeschoben worden sein. Ihn zu senden und die Antwort
   * nicht anzusehen ist der häufigste halbe Einbau von OIDC.
   */
  if (ansprueche["nonce"] !== angefangen.einmalwert) {
    return {
      art: "fehler",
      grund: "Der Einmalwert im Token gehört nicht zu dieser Anmeldung.",
    };
  }

  merkeToken(token);
  return {
    art: "angemeldet",
    wer: {
      name: typeof ansprueche["name"] === "string" ? ansprueche["name"] : "",
      benutzername:
        typeof ansprueche["preferred_username"] === "string"
          ? ansprueche["preferred_username"]
          : "",
      gruppen: Array.isArray(ansprueche["groups"])
        ? (ansprueche["groups"] as unknown[]).filter(
            (g): g is string => typeof g === "string",
          )
        : [],
      ansprueche,
      token,
    },
    weiter: angefangen.zurueckNach,
  };
}

/**
 * Meldet ab — bei Keycloak, nicht nur hier.
 *
 * Das lokale Vergessen allein macht die Sitzung bei Keycloak nicht ungültig:
 * Der nächste Aufruf käme ohne Kennworteingabe zurück, und wer den Rechner
 * verlässt, hätte sich nicht abgemeldet, sondern nur die Seite geschlossen.
 */
export function meldeAb(
  anbieter: IdentitaetsAnbieter,
  herkunft: string,
): void {
  const token = liesToken();
  vergissToken();
  vergissAngefangen();
  window.location.assign(
    abmeldeUrl(anbieter, {
      ...(token ? { idToken: token.idToken } : {}),
      zurueckNach: herkunft + "/",
    }),
  );
}
